import { useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { ImageIcon, ArrowLeft, ArrowRight, ArrowUpRight, X } from "lucide-react";
import { Reveal, Overline, MaskReveal } from "@/components/Motion";
import { getProject } from "@/lib/projects";

const ImagePlaceholder = ({ className = "", label = "Project Image" }) => (
  <div className={`relative w-full overflow-hidden bg-evo-marble1 border border-white/8 ${className}`}>
    <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
      <ImageIcon size={32} className="text-evo-gold/40 mb-4" strokeWidth={1} />
      <p className="text-xs uppercase tracking-[0.25em] text-evo-ash/50">{label}</p>
      <p className="text-[10px] uppercase tracking-[0.2em] text-evo-ash/30 mt-2">Awaiting approved photo</p>
    </div>
    <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "repeating-linear-gradient(45deg, #C8A97E 0 1px, transparent 1px 14px)" }} />
  </div>
);

const NotFound = () => (
  <div data-testid="experience-notfound" className="min-h-screen flex flex-col items-center justify-center px-6 text-center">
    <p className="font-serif text-4xl text-evo-pearl mb-6">Project not found</p>
    <Link to="/experiences" className="link-underline text-xs uppercase tracking-[0.2em] text-evo-gold">Back to Portfolio</Link>
  </div>
);

const ExperienceDetail = () => {
  const { id } = useParams();
  const { project, index, next } = getProject(id);
  const [zoom, setZoom] = useState(null);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);

  if (!project) return <NotFound />;

  const num = String(index + 1).padStart(2, "0");
  const meta = [
    ["Exhibition", project.exhibitionName],
    ["Location", project.location],
    ["Industry", project.industry],
  ].filter(([, v]) => v);

  return (
    <div data-testid="experience-detail-page">
      {/* Full-bleed hero */}
      <section ref={heroRef} className="relative h-screen min-h-[620px] flex items-end overflow-hidden">
        <motion.div style={{ y, scale }} className="absolute inset-0 will-change-transform" data-cursor={project.image ? "view" : undefined} onClick={() => project.image && setZoom(project.image)}>
          {project.image ? (
            <img src={project.image} alt={project.projectName || "EVO project"} style={{ objectPosition: project.crop || "center" }} className="w-full h-full object-cover" />
          ) : (
            <ImagePlaceholder className="h-full" label="Hero Image" />
          )}
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-t from-evo-black via-evo-black/40 to-evo-black/20 pointer-events-none" />
        <div className="relative z-10 px-6 md:px-12 lg:px-20 pb-16 md:pb-24 w-full pointer-events-none">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15 }}>
            <Overline className="block mb-6">Selected Experience · {num}</Overline>
          </motion.div>
          <h1 className="font-serif font-light text-5xl md:text-7xl lg:text-8xl tracking-tight leading-[0.95] text-evo-pearl max-w-4xl">
            <MaskReveal delay={0.25}>{project.projectName || "EVO Project"}</MaskReveal>
          </h1>
        </div>
      </section>

      {/* Meta bar (only filled fields) */}
      {meta.length > 0 && (
        <section className="px-6 md:px-12 lg:px-20 py-16 border-b border-white/8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {meta.map(([label, val], i) => (
              <Reveal key={label} delay={i * 0.06}>
                <p className="text-[11px] uppercase tracking-[0.25em] text-evo-gold mb-3">{label}</p>
                <p className="font-serif text-xl md:text-2xl text-evo-pearl">{val}</p>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* Editorial case study */}
      {(project.overview || project.evoSolution || project.impact || (project.services && project.services.length > 0)) && (
        <section className="px-6 md:px-12 lg:px-20 py-24 md:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-4">
              <Reveal><Overline className="block mb-8">Case Study</Overline></Reveal>
              {project.services && project.services.length > 0 && (
                <Reveal delay={0.1}>
                  <p className="text-[11px] uppercase tracking-[0.25em] text-evo-gold mb-4">Services Delivered</p>
                  <div className="flex flex-wrap gap-2">
                    {project.services.map((s) => (
                      <span key={s} className="text-xs text-evo-pearl border border-white/15 px-3 py-1.5">{s}</span>
                    ))}
                  </div>
                </Reveal>
              )}
            </div>
            <div className="lg:col-span-8 space-y-12">
              {project.overview && (
                <Reveal>
                  <p className="text-[11px] uppercase tracking-[0.25em] text-evo-gold mb-4">Project Overview</p>
                  <p className="font-serif font-light text-2xl md:text-3xl leading-[1.3] text-evo-pearl text-balance">{project.overview}</p>
                </Reveal>
              )}
              {project.evoSolution && (
                <Reveal>
                  <p className="text-[11px] uppercase tracking-[0.25em] text-evo-gold mb-4">EVO Solution</p>
                  <p className="text-evo-ash font-light text-lg leading-relaxed">{project.evoSolution}</p>
                </Reveal>
              )}
              {project.impact && (
                <Reveal>
                  <p className="text-[11px] uppercase tracking-[0.25em] text-evo-gold mb-4">Exhibition Impact</p>
                  <p className="text-evo-ash font-light text-lg leading-relaxed">{project.impact}</p>
                </Reveal>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Full-bleed image story */}
      <section className="space-y-6 md:space-y-8 py-8">
        {project.gallery.map((img, i) => {
          const full = i % 3 === 0;
          return (
            <Reveal key={i} y={30} className={full ? "" : "px-6 md:px-12 lg:px-20"}>
              {img ? (
                <div
                  className={`overflow-hidden cursor-pointer ${full ? "aspect-[21/9]" : "aspect-[16/9]"}`}
                  data-cursor="view"
                  onClick={() => setZoom(img)}
                >
                  <img src={img} alt="" className="w-full h-full object-cover transition-transform duration-[1.4s] hover:scale-105" />
                </div>
              ) : (
                <ImagePlaceholder className={full ? "aspect-[21/9]" : "aspect-[16/9]"} label={`Story Image ${i + 1}`} />
              )}
            </Reveal>
          );
        })}
      </section>

      {/* Next + CTA */}
      <section className="px-6 md:px-12 lg:px-20 py-24 border-t border-white/8">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-10">
          <Link to="/experiences" data-testid="back-to-experiences" className="inline-flex items-center gap-3 link-underline text-xs uppercase tracking-[0.2em] text-evo-ash hover:text-evo-pearl group">
            <ArrowLeft size={15} className="transition-transform duration-300 group-hover:-translate-x-1" /> All Projects
          </Link>
          <Link to={`/experiences/${next.id}`} data-testid="next-experience" className="group text-right">
            <p className="text-[11px] uppercase tracking-[0.25em] text-evo-gold mb-2">Next Project</p>
            <span className="font-serif text-3xl md:text-4xl text-evo-pearl group-hover:text-evo-gold transition-colors inline-flex items-center gap-3">
              {next.projectName || "EVO Project"}
              <ArrowRight size={22} className="transition-transform duration-300 group-hover:translate-x-1" />
            </span>
          </Link>
        </div>

        <div className="mt-20 bg-evo-marble1 border border-white/5 p-10 md:p-16 flex flex-col md:flex-row md:items-center justify-between gap-8">
          <h2 className="font-serif font-light text-3xl md:text-5xl text-evo-pearl">Envision your stand with EVO.</h2>
          <Link to="/contact" data-testid="detail-cta-btn" className="inline-flex items-center gap-3 bg-evo-gold text-evo-black hover:bg-evo-goldsoft transition-colors duration-300 px-9 py-5 text-xs uppercase tracking-[0.2em] group shrink-0">
            Request a Quotation
            <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>
      </section>

      {/* Zoom viewer */}
      <AnimatePresence>
        {zoom && (
          <motion.div
            data-testid="detail-lightbox"
            className="fixed inset-0 z-[120] bg-evo-black/95 backdrop-blur-md flex items-center justify-center p-6 md:p-16"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setZoom(null)}
          >
            <button data-testid="detail-lightbox-close" className="absolute top-6 right-6 text-evo-pearl hover:text-evo-gold transition-colors p-2" data-cursor="view">
              <X size={28} />
            </button>
            <motion.img
              src={zoom} alt="" className="max-w-full max-h-full object-contain"
              initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ExperienceDetail;
